// vendors/farlo-ui-lazyload.js
class FarloUiLazyLoad {
	constructor(container = document, options = {}) {
		this.container = container;
		this.options = Object.assign({
			rootMargin: '300px 0px',
			threshold: 0.01,
			observeMutations: true
		}, options);

		this.observed = new WeakSet();
		this.observer = null;

		this.init();
	}

	init() {
		this.ensureObserver();
		this.observeTargets(this.collectTargets());

		if (this.options.observeMutations) {
			const mo = new MutationObserver((muts) => {
				muts.forEach((m) => {
					m.addedNodes.forEach((node) => {
						if (!(node instanceof Element)) {
							return;
						}
						if (node.classList && node.classList.contains('lazy')) {
							this.observeTargets([node]);
						}
						this.observeTargets(this.collectTargets(node));
					});
				});
			});
			mo.observe(this.container, { childList: true, subtree: true });
		}
	}

	collectTargets(root = this.container) {
		return Array.from(root.querySelectorAll('.lazy'));
	}

	ensureObserver() {
		if (this.observer) {
			return;
		}
		if ('IntersectionObserver' in window) {
			this.observer = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting || entry.intersectionRatio > 0) {
						this.observer.unobserve(entry.target);
						this.loadElement(entry.target);
					}
				});
			}, {
				root: null,
				rootMargin: this.options.rootMargin,
				threshold: this.options.threshold
			});
		} else {
			this.observer = { observe: (el) => this.loadElement(el), unobserve() {} };
		}
	}

	observeTargets(targets) {
		targets.forEach((el) => {
			if (!this.observed.has(el) && !el._lazyLoaded) {
				this.observed.add(el);
				this.observer.observe(el);
			}
		});
	}

	loadElement(el) {
		if (!el || el._lazyLoaded) {
			return;
		}
		const tag = el.tagName;

		switch (tag) {
			case 'IMG': this.loadImage(el); break;
			case 'VIDEO':
			case 'AUDIO': this.loadMedia(el); break;
			case 'IFRAME': this.loadIframe(el); break;
			default: this.loadBackground(el);
		}

		el.classList.remove('lazy');
		el._lazyLoaded = true;
	}

	loadImage(el) {
		const supportsDecode = 'decode' in HTMLImageElement.prototype;
		const supportsReplaceWith = 'replaceWith' in HTMLElement.prototype;

		if (el.parentNode && el.parentNode.tagName === 'PICTURE') {
			el.parentNode.querySelectorAll('source').forEach((src) => this.flipDataAttrs(src));
		}

		if (supportsDecode && supportsReplaceWith) {
			const newImg = new Image();
			this.flipDataAttrs(newImg, el);
			newImg.decode().then(() => {
				[...el.attributes].forEach((attr) => {
					if (!/^data-/.test(attr.name)) {
						newImg.setAttribute(attr.name, attr.value);
					}
				});
				el.replaceWith(newImg);
			}).catch(() => this.flipDataAttrs(el));
		} else {
			this.flipDataAttrs(el);
		}
	}

	loadMedia(el) {
		if (el.dataset.src) {
			el.src = el.dataset.src;
			el.removeAttribute('data-src');
		}
		el.querySelectorAll('source').forEach((src) => this.flipDataAttrs(src));
		if (typeof el.load === 'function') {
			el.load();
		}
	}

	loadIframe(el) {
		if (el.dataset.src) {
			el.src = el.dataset.src;
			el.removeAttribute('data-src');
		} else {
			this.flipDataAttrs(el);
		}
	}

	loadBackground(el) {
		if (el.dataset.bg) {
			el.style.backgroundImage = `url("${ el.dataset.bg }")`;
			el.removeAttribute('data-bg');
		}
		if (el.dataset.bgset) {
			el.style.backgroundImage = `image-set(${ el.dataset.bgset })`;
			el.removeAttribute('data-bgset');
		}
		if (el.dataset.src) {
			el.style.backgroundImage = `url("${ el.dataset.src }")`;
			el.removeAttribute('data-src');
		}
	}

	flipDataAttrs(el, refEl) {
		const sourceDataset = refEl ? refEl.dataset : el.dataset;
		for (const key in sourceDataset) {
			const full = 'data-' + key;
			if (['data-src', 'data-sizes', 'data-media', 'data-srcset'].includes(full)) {
				el.setAttribute(key, sourceDataset[ key ]);
				el.removeAttribute(full);
			}
		}
	}
}

export default FarloUiLazyLoad;
